<script setup lang="ts">
/**
 * A confirmation before something the user may not have meant to start.
 *
 * The first overlay in the app. DESIGN.md section 9 lists "Modal / overlay" as
 * demanded by `REF-06` and `ORG-07` as well, so this is shaped as the confirm
 * case of that component rather than as a sign-out dialog — `tone` is the only
 * thing a destructive caller has to change.
 *
 * **A native `<dialog>` opened with `showModal()`, not a div with a high
 * z-index.** The same argument as the radios in `ThemeMenu`: everything that is
 * hard about a modal already exists in the platform and is hard to get right by
 * hand. `showModal()` gives the top layer, the focus trap, `inert` on
 * everything behind it, Escape, `::backdrop`, and — the one that is almost
 * always missed — returning focus to whatever opened it when it closes.
 *
 * **Cancel takes focus, not the confirm button.** The whole point is a control
 * somebody reached by accident, so the key that dismisses dialogs and the key
 * that activates the focused button must not be the same key. `autofocus` says
 * so in the markup rather than relying on Cancel happening to come first.
 *
 * **`open` stays the caller's.** Every dismissal — Escape, the scrim, Cancel —
 * emits `cancel` and lets the caller flip its own state, so there is one source
 * of truth for whether this is on screen. Escape is not prevented, so a caller
 * that forgets to handle `cancel` still leaves a dialog the user can escape.
 */

import { onMounted, ref, useId, watch } from 'vue'

import MpButton from './MpButton.vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    confirmLabel: string
    cancelLabel?: string
    /** `critical` for something irreversible. Sign-out is not that. */
    tone?: 'primary' | 'critical'
  }>(),
  { cancelLabel: 'Cancel', tone: 'primary' }
)

const emit = defineEmits<{ confirm: []; cancel: [] }>()

const titleId = useId()
const bodyId = useId()

const element = ref<HTMLDialogElement | null>(null)

/**
 * `hasAttribute` rather than the `open` property, and a guarded `showModal`,
 * because jsdom 25 renders `<dialog>` as an ordinary unknown element: no
 * `showModal`, no `close`, no `open` IDL attribute. Reflecting the attribute by
 * hand is what lets the shell's tests click through this dialog at all. It is a
 * test-environment fallback and nothing else — every browser this ships to has
 * had `showModal` since 2022, and the attribute path has no focus trap, so it
 * must never become the normal route.
 */
function sync (): void {
  const el = element.value
  if (!el) return
  const shown = el.hasAttribute('open')
  if (props.open && !shown) {
    if (typeof el.showModal === 'function') el.showModal()
    else el.setAttribute('open', '')
  } else if (!props.open && shown) {
    if (typeof el.close === 'function') el.close()
    else el.removeAttribute('open')
  }
}

onMounted(sync)
watch(() => props.open, sync, { flush: 'post' })

/**
 * A click on the scrim lands on the `<dialog>` itself, because everything
 * visible is inside the panel. Dismissing is the safe direction for a
 * confirmation, so the scrim cancels.
 */
function onDialogClick (event: MouseEvent): void {
  if (event.target === element.value) emit('cancel')
}
</script>

<template>
  <dialog
    ref="element"
    class="mp-dialog"
    role="alertdialog"
    :aria-labelledby="titleId"
    :aria-describedby="bodyId"
    @cancel="emit('cancel')"
    @click="onDialogClick"
  >
    <div class="mp-dialog__panel">
      <h2
        :id="titleId"
        class="mp-dialog__title"
      >
        {{ props.title }}
      </h2>

      <p
        :id="bodyId"
        class="mp-dialog__body"
      >
        <slot />
      </p>

      <div class="mp-dialog__actions">
        <MpButton
          autofocus
          @click="emit('cancel')"
        >
          {{ props.cancelLabel }}
        </MpButton>
        <MpButton
          :variant="props.tone"
          @click="emit('confirm')"
        >
          {{ props.confirmLabel }}
        </MpButton>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
/* The element is the scrim. `margin: auto` is what centers a top-layer dialog;
   flexbox on it would center the panel and leave the scrim the size of the
   panel. */
.mp-dialog {
  max-inline-size: min(32rem, calc(100vw - 2 * var(--space-4)));
  margin: auto;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  background: var(--surface-raised);
  color: var(--text-primary);
  box-shadow: var(--shadow-overlay);
}

.mp-dialog::backdrop {
  background: var(--surface-scrim);
}

.mp-dialog__panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--card-pad);
}

.mp-dialog__title {
  font-size: var(--font-size-h4);
}

.mp-dialog__body {
  color: var(--text-secondary);
  font-size: var(--font-size-small);
}

/* Confirm last, so the button under the reading order's end is the one the
   sentence above it has just described. At 360 they stack and the safe one is
   still first. */
.mp-dialog__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-3);
}

/* `<dialog>` goes from `display: none` to `display: block`, so there is nothing
   for a transition to interpolate — an entrance has to be an animation, which
   runs when the element is first displayed. The tokens collapse to 1ms under
   `prefers-reduced-motion`, and the block at the foot of `styles/base.css`
   catches it again. */
.mp-dialog[open] {
  animation: mp-dialog-in var(--motion-base) var(--motion-ease);
}

.mp-dialog[open]::backdrop {
  animation: mp-dialog-scrim-in var(--motion-base) var(--motion-ease);
}

@keyframes mp-dialog-in {
  from {
    opacity: 0;
    translate: 0 var(--space-2);
    scale: 0.98;
  }
}

@keyframes mp-dialog-scrim-in {
  from {
    opacity: 0;
  }
}
</style>
